import React, { useEffect } from 'react';
import { useTransportationOptions } from '../../API/GoMoto';
import { useAppointmentState } from '../../context/Appointment/appointment';
import { useCart } from '../../context/cart';
import { useKioskImage, useKioskState } from '../../context/Kiosk/kiosk';
import { useLocalization } from '../../context/Localization/localization';
import { useHideKioskBackground, usePageDispatch } from '../../context/Page/page';
import { CTAButton } from '../../controls/ButtonBase';
import { Heading } from '../../controls/Heading/Heading';
import { KioskImageDisplay } from '../../controls/KioskImageDisplay';
import { TransportSubtype } from '../../API/Enums';
import { BaseIdleSessionTimeout } from '../../helpers/timeout';
import { DisplayModel } from '../../helpers/displayVehicle';
import { ScrollableBody } from '../Layout/ScrollableBody';
import css from './Complete.module.scss';
import { Types } from '../../context/Page/page.types';
import { GMButtonGroup } from '../../controls/ButtonGroup';
import { GMRoundedIcon, GMSizedIcon } from '../../controls/Icon';
import SizedIcon from '../../controls/Icon/SizedIcon/SizedIcon';

export interface CompleteProps {
    /**
     * Passes the resetKiosk for when the user clicks to confirm check-in is complete.
     */
    onComplete: () => void;
}

export const Complete: React.FC<CompleteProps> = React.memo(({ onComplete }) => {
    useHideKioskBackground()();
    const Localization = useLocalization();
    const { transportation } = useCart();
    const {
        key: kioskKey,
        timeout,
        kioskType,
        dealer: { key: dealerKey, localization: { fullKioskInstructions } },
    } = useKioskState();

    const { transportationOptions } = useTransportationOptions(kioskKey, dealerKey);
    let closingMessage = '';
    const idleTimeout = timeout || BaseIdleSessionTimeout;
    const appointment = useAppointmentState();
    const pageDispatch = usePageDispatch();
    const emailImage = useKioskImage('Email');
    const textImage = useKioskImage('Text');
    const arrowImage = useKioskImage('Arrow');

    if (transportation && transportationOptions) {
        const selected = transportationOptions.find((o) => o.type === transportation.type && o.subtype === (transportation.subtype || TransportSubtype.none));
        closingMessage = selected?.localization.closingMessage || '';
    }

    useEffect(() => {
        if (kioskType !== 'outdoor') {
            pageDispatch({
                type: Types.HideIdleTimeoutDialog,
                payload: true,
            });

            return () => {
                pageDispatch({
                    type: Types.HideIdleTimeoutDialog,
                    payload: false,
                });
            };
        }

        return;
    }, [pageDispatch, kioskType]);

    return (
        <ScrollableBody
            heading={
                <Heading
                    roundedIcon={
                        <GMRoundedIcon roundedBackgroundSize={'large'} themeType={'secondary'}>
                            <GMSizedIcon
                                ariaLabel={Localization.CompleteCheckmarkIcon}
                                iconType={'Complete'}
                                iconSize={'extraLarge'}
                            />
                        </GMRoundedIcon>
                    }
                    primaryHeading={Localization.CheckInComplete}
                    secondaryHeading={
                        kioskType !== 'outdoor'
                            ? Localization.ServiceAdvisorNotifiedArrived
                            : Localization.formatString(Localization.OutdoorCheckInComplete, <b>{appointment.year ?? ''}</b>, <b>{DisplayModel(appointment)}</b>)
                    }
                    showMustache
                />
            }
            proceedButtons={
                <GMButtonGroup
                    buttons={[
                        <CTAButton
                            styleType={'primary'}
                            onClick={onComplete}
                            title={Localization.ClickToConfirmCheckInComplete}
                            label={Localization.Finish}
                            kioskOptions={{
                                inlineOptions: {
                                    sizedIcon: (
                                        <SizedIcon
                                            iconSize={'custom'}
                                            iconType={'Complete'}
                                            ariaLabel={Localization.CompleteCheckmarkIcon}
                                        />
                                    ),
                                },
                            }}
                            id={Localization.ClickToConfirmCheckInComplete}
                            ariaLabel={Localization.ClickToConfirmCheckInComplete}
                        />,
                    ]}
                />
            }
        >
            <div className={css.closingMessage}>
                {(kioskType === 'outdoor' || kioskType === 'express') && <div className={css.message}>
                    {fullKioskInstructions}
                </div>}
                <div className={css.message}>
                    {closingMessage}
                </div>
            </div>
            <div className={css.center}>
                {kioskType !== 'outdoor' && kioskType !== 'express' && (
                    <>
                        <KioskImageDisplay
                            left={{
                                image: appointment.contactPreference === 'text' ? textImage : emailImage,
                                alt: appointment.contactPreference === 'text' ? Localization.SendTextIcon : Localization.SendEmailIcon,
                            }}
                            right={{
                                top: {
                                    text: appointment.contactPreference === 'text' ? Localization.CheckYourTexts : Localization.CheckYourEmail,
                                },
                                image: arrowImage,
                                alt: Localization.ProceedArrow,
                                bottom: Localization.ServiceAdvisorNotified,
                            }}
                        />
                        <div>{Localization.formatString(Localization.ThisPageWillRefresh, { seconds: idleTimeout })}</div>
                    </>
                )}
            </div>
        </ScrollableBody>
    );
});
